import React, { useEffect, useCallback } from 'react';
import { Checkbox, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FilterItem, SelectedFilters } from '../../types';
import { useURLState } from '../../hooks/useURLState';

const CustomCheckboxColor = styled(Checkbox)({
  color: 'rgba(0, 0, 0, 0.54)',
  width: '24px',
  height: '24px',
  padding: '4px',
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
    width: '24px',
    height: '24px',
  },
  '& input[type="checkbox"]': {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
  },
  '&.Mui-checked': {
    color: 'inherit',
  },
});

interface FilterProps {
  filters: FilterItem[];
  name: string;
  selectedCategories: SelectedFilters;
  setSelectedCategories: (categories: SelectedFilters) => void;
  toggleFilter: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setParamTags: (tags: string) => void;
}

const Filter: React.FC<FilterProps> = React.memo(
  ({ filters, name, selectedCategories, setSelectedCategories, toggleFilter, setParamTags }) => {
    const { getParameter } = useURLState();

    useEffect(() => {
      const filterParams = getParameter('filter');
      setParamTags(filterParams?.split('&').join(',') ?? '');
      const initialSelectedCategories: SelectedFilters = {};

      if (filterParams) {
        filterParams.split('&').forEach((id) => {
          initialSelectedCategories[id] = true;
        });
      }

      setSelectedCategories(initialSelectedCategories);
    }, [getParameter, setParamTags, setSelectedCategories]);

    const handleCheckboxChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        // The parent component (App.tsx) handles URL updates through toggleFilter
        // This just calls the parent's toggle function which manages URL state
        toggleFilter(event);
      },
      [toggleFilter]
    );

    const htmlDecode = useCallback((input: string): string => {
      const doc = new DOMParser().parseFromString(input, 'text/html');
      return doc.documentElement.textContent ?? '';
    }, []);

    if (filters.length < 1) {
      return null;
    }

    return (
      <>
        <div className="blog-index-filters">
          <InputLabel className="blog-index-filters-title" color="secondary">
            <b>{name}</b>
          </InputLabel>
          {filters.map((filter) => {
            const isChecked = !!selectedCategories[filter.id];
            return (
              <div key={filter.id}>
                <CustomCheckboxColor
                  value={filter.id}
                  onChange={handleCheckboxChange}
                  checked={isChecked}
                />
                <span>{htmlDecode(filter.name)}</span>
              </div>
            );
          })}
        </div>
        <br />
      </>
    );
  }
);

export default Filter;
